/**
 * Pure formatters for the desktop self-update download modal (<UpdateBanner/>).
 *
 * Kept side-effect-free and dependency-free so they can be unit-tested in
 * isolation (downloadFormat.test.ts) and reused anywhere a human-readable
 * byte / rate / ETA readout is needed. Lives in lib/notifications/ next to
 * desktopBridge / useDesktopUpdate (lowercase filename — no Foo.tsx collision).
 */

const KB = 1024;
const MB = KB * 1024;
const GB = MB * 1024;

/**
 * Human-readable byte size: "0 B", "812 KB", "12.4 MB", "1.07 GB".
 * Sub-MB shows whole KB; MB/GB show 1–2 decimals so a download visibly ticks
 * up between progress events. Negative / non-finite inputs clamp to "0 B".
 */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < KB) return `${Math.round(bytes)} B`;
  if (bytes < MB) return `${Math.round(bytes / KB)} KB`;
  if (bytes < GB) return `${(bytes / MB).toFixed(1)} MB`;
  return `${(bytes / GB).toFixed(2)} GB`;
}

/**
 * The headline "downloaded of total" line.
 *   - total known   → "12.4 MB / 48.0 MB"
 *   - total unknown → "12.4 MB downloaded"   (some servers/CDNs omit a
 *     Content-Length; we still show live progress)
 *   - nothing yet   → "Starting download…"
 */
export function formatProgressLine(
  downloaded: number,
  contentLength: number | null,
): string {
  if (contentLength != null && contentLength > 0) {
    return `${formatBytes(downloaded)} / ${formatBytes(contentLength)}`;
  }
  if (downloaded > 0) return `${formatBytes(downloaded)} downloaded`;
  return "Starting download…";
}

/** Transfer rate, e.g. "1.8 MB/s". `null`/non-positive → null (nothing shown). */
export function formatSpeed(bytesPerSecond: number | null): string | null {
  if (bytesPerSecond == null || !Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) {
    return null;
  }
  return `${formatBytes(bytesPerSecond)}/s`;
}

/**
 * Rough remaining time from the bytes left and the current rate, e.g.
 * "~12s left", "~1m 30s left". Needs a known total AND a positive rate;
 * otherwise null. Capped so a momentary near-zero rate can't print an
 * absurd ETA.
 */
export function formatEta(
  downloaded: number,
  contentLength: number | null,
  bytesPerSecond: number | null,
): string | null {
  if (
    contentLength == null ||
    bytesPerSecond == null ||
    !Number.isFinite(bytesPerSecond) ||
    bytesPerSecond <= 0
  ) {
    return null;
  }
  const remaining = contentLength - downloaded;
  if (remaining <= 0) return null;
  const seconds = Math.round(remaining / bytesPerSecond);
  if (seconds <= 0) return null;
  if (seconds < 60) return `~${seconds}s left`;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins < 60) return secs ? `~${mins}m ${secs}s left` : `~${mins}m left`;
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  return remMins ? `~${hours}h ${remMins}m left` : `~${hours}h left`;
}

/**
 * The secondary caption under the progress bar: percent · speed · ETA,
 * joined with " · " and dropping any unavailable piece. Empty string when
 * nothing is known yet (caller renders nothing).
 */
export function formatProgressCaption(
  percent: number | null,
  downloaded: number,
  contentLength: number | null,
  bytesPerSecond: number | null,
): string {
  const parts: string[] = [];
  if (percent != null && Number.isFinite(percent)) parts.push(`${percent}%`);
  const speed = formatSpeed(bytesPerSecond);
  if (speed) parts.push(speed);
  const eta = formatEta(downloaded, contentLength, bytesPerSecond);
  if (eta) parts.push(eta);
  return parts.join(" · ");
}
